<template>
  <div class="v3-widget v3-panel v3-prog" v-testid="testid">
    <div class="v3-panel-head"><h3 v-testid="testid + '-title'">{{ dc.headingDisplay.value }}</h3></div>

    <div v-if="dc.showSwitcher.value" class="v3-seg-row">
      <div class="v3-seg" role="tablist" aria-label="Document type">
        <button
          v-for="t in dc.docTypes.value"
          :key="t"
          type="button"
          role="tab"
          :aria-selected="t === dc.activeType.value"
          class="v3-seg-btn"
          :class="{ 'is-on': t === dc.activeType.value }"
          v-testid="testid + '-tab-' + t"
          @click="dc.setType(t)"
        >{{ t }}</button>
      </div>
    </div>

    <V3Skeleton v-if="dc.loading.value && !dc.segmentsResolved.value.length" shape="donut" :heading="false" />
    <!-- Bar mode: stacked completion bar + legend (prototype V3Progress mode='bar'). -->
    <div v-else-if="dc.mode.value === 'bar'" class="v3-panel-pad" :class="{ 'is-busy': dc.busy.value }">
      <div class="v3-prog-head">
        <span class="v3-prog-total">{{ dc.centreValueDisplay.value }}</span>
        <span v-if="dc.centreLabelDisplay.value" class="v3-prog-unit">{{ dc.centreLabelDisplay.value }}</span>
        <span v-if="dc.insightText.value" class="v3-prog-insight">{{ dc.insightText.value }}</span>
      </div>
      <div class="v3-prog-bar" role="img" :aria-label="dc.headingDisplay.value">
        <span
          v-for="(s, i) in dc.segmentsResolved.value"
          :key="i"
          class="v3-prog-seg"
          :style="{ width: dc.pct(s.value) + '%', background: s.colour }"
          :title="s.label + ': ' + s.value"
        ></span>
      </div>
      <div class="v3-prog-legend">
        <span v-for="(s, i) in dc.segmentsResolved.value" :key="i" class="v3-prog-leg">
          <span class="dot" :style="{ background: s.colour }"></span>
          <span class="lbl">{{ s.label }}</span>
          <span class="n">{{ s.value }}</span>
          <span class="pc">{{ dc.pct(s.value) }}%</span>
        </span>
      </div>
    </div>
    <div v-else class="v3-panel-pad" :class="{ 'is-busy': dc.busy.value }">
      <div class="v3-donut-row">
        <div class="v3-donut-wrap">
          <svg width="128" height="128" viewBox="0 0 132 132" class="v3-donut" aria-hidden="true">
            <circle class="v3-donut-track" cx="66" cy="66" r="52" fill="none" stroke-width="13" />
            <circle
              v-for="(a, i) in dc.arcs.value"
              :key="i"
              cx="66"
              cy="66"
              r="52"
              fill="none"
              stroke-width="13"
              :style="{ stroke: a.colour }"
              :stroke-dasharray="a.len + ' ' + (dc.circ.value - a.len)"
              :stroke-dashoffset="-a.off"
              transform="rotate(-90 66 66)"
            />
          </svg>
          <div class="v3-donut-centre">
            <span class="v">{{ dc.centreValueDisplay.value }}</span>
            <span class="u" :title="dc.centreLabelDisplay.value">{{ dc.centreLabelDisplay.value }}</span>
          </div>
        </div>
        <div class="v3-donut-legend">
          <span v-for="(s, i) in dc.segmentsResolved.value" :key="i" class="v3-donut-leg">
            <span class="dot" :style="{ background: s.colour }"></span>
            <span class="lbl">{{ s.label }}</span>
            <span class="n">{{ s.value }}</span>
            <span class="pc">{{ dc.pct(s.value) }}%</span>
          </span>
        </div>
      </div>
    </div>
    <button v-if="dc.linkTextDisplay.value && linkAllowed" type="button" class="v3-prog-link" v-testid="testid + '-link'" @click="onLink">
      {{ dc.linkTextDisplay.value }}<span class="ms">chevron_right</span>
    </button>
  </div>
</template>

<script>
import axios from "axios";
import { donutChart } from "@rm-ui/homepage/donutChart";
import { canNavigateTo } from "@/composables/useV3Nav";
import V3Skeleton from "./cLandingV3Skeleton";

// New-portal (v3) DonutChart / Progress — thin Vue 2 presentation over the
// shared `donutChart` composable (@rm-ui/homepage/donutChart). Segment
// resolution, doc-type switcher, {{docType}} interpolation and arc geometry live
// in the composable; this file owns navigation. Sibling presentation:
// rm-portal-beta DonutChartWidget.vue.
export default {
  name: "cLandingV3DonutChart",
  components: { V3Skeleton },
  props: {
    definition: { type: Object, default: () => ({}) },
    editMode: { type: Boolean, default: false },
    qaActions: { type: Array, default: () => [] },
  },
  setup(props) {
    const dc = donutChart({
      http: axios,
      getDefinition: () => props.definition || {},
    });
    return { dc };
  },
  computed: {
    testid() {
      return this.definition.controlType || "v3DonutChart";
    },
    // Footer link is permission-gated (legacy landing-config parity); the
    // editor shows it regardless.
    linkAllowed() {
      const dest = this.definition.linkUrl || "";
      return this.editMode || !dest || canNavigateTo(this, dest);
    },
  },
  methods: {
    onLink() {
      if (this.editMode) return;
      const dest = this.definition.linkUrl;
      if (!dest) return;
      if (/^https?:\/\//i.test(dest)) {
        window.open(dest, "_blank");
        return;
      }
      this.$router.push({ name: dest }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" src="./v3Widgets.scss"></style>
